
import { defineAsyncComponent, defineComponent } from 'vue';

/** Utils */
import { listTypeslogin, listCountrys } from '@/utils/auth.utils';
import { showSwalInfo, showSwalError } from '@/utils/sweetalert2-utils';
import { showErrors } from '@/utils/general-utils';
import Swal from 'sweetalert2';

/** Interfaces */
import { LoginI, SendSmsI } from '@/interfaces/auth.interface';

/** reCAPTCHA */
import { renderReCaptchaWidget } from '@artifact-project/recaptcha';

/** Components */
import CookieConsent from '@/components/layout/CookieConsent.vue';
import RefinancaBanner from '@/components/layout/RefinancaBanner.vue';
import OfficialCommunicationPopup from '@/components/shared/OfficialCommunicationPopup.vue';
import { Field, Form } from 'vee-validate';

/** Validations */
import { schemaLoginForm } from '@/validations/login-validation';

export default defineComponent({
  name: 'Login',
  props: {
    token: {
      type: String,
      required: false,
      default: '',
    },
  },
  components: {
    ModalConfirmSms: defineAsyncComponent(
      () => import('@/components/shared/ModalConfirmSms.vue')
    ),
    ModalContactChannels: defineAsyncComponent(
      () => import('@/components/shared/ModalContactChannels.vue')
    ),
    Form,
    Field,
    CookieConsent,
    RefinancaBanner,
    OfficialCommunicationPopup,
  },
  data() {
    const dataForm: SendSmsI = {};

    return {
      isActive: false,
      isCookieVisible: true,
      selectTypes: listTypeslogin,
      selectCountry: listCountrys,
      schemaLoginForm: schemaLoginForm,
      selectCountryDefault: listCountrys[0].value,
      selectDefault: listTypeslogin[0].value,
      successReCaptcha: false,
      showModalSms: false,
      showModalContactChannels: false,
      contactChannelsTitle: '',
      contactChannelsDescription: '',
      dataForm: dataForm,
      showRecaptcha: process.env.VUE_APP_SHOW_RECAPTCHA === 'true',
      sendingLoading: false,
      sendingSuccess: false,
      loginLoading: false,
    };
  },
  methods: {
    onCookieStatusChange(isActive: boolean) {
      this.isCookieVisible = isActive;
    },

    sendSms(form: SendSmsI) {
      form = {
        ...form,
        country_code: this.selectCountryDefault,
      };
      /*this.$store.dispatch('startTransactionApm', {
        apm: this.$apm,
        name: 'click - ingresar',
        type: 'custom',
        payload: form,
      });*/
      this.sendingLoading = true;
      this.$store
        .dispatch('sendSms', form)
        .then((res) => {
          console.log('Respuesta: ', res);
          this.$store.dispatch('addTransaction', res.data);
          const { success, token = false, emails = [] } = res.data;

          if (success) {
            if (!token) {
              this.dataForm = form;
              this.openModalConfirmSms();
              this.sendingSuccess = true;
              setTimeout(() => (this.sendingSuccess = false), 4000);
            } else {
              this.showEmailsSentModal(emails);
            }
          } else {
            showSwalError(
              'No fue posible enviar el mensaje, intente más tarde.'
            );
          }
        })
        .catch((error) => {
          console.log('ERROR COMPLETO: ', error);
          const errorData = error?.response?.data?.error;

          if (
            error.response?.status === 422 &&
            errorData?.show_contact_channels
          ) {
            this.openModalContactChannels(
              errorData.title[0],
              errorData.message[0]
            );
          } else if (
            error.response?.status === 422 &&
            Object.prototype.hasOwnProperty.call(
              error.response.data,
              'error'
            ) &&
            Object.prototype.hasOwnProperty.call(
              error.response.data.error,
              'link'
            )
          ) {
            this.openModalWhatAppLink(
              error.response.data.error.title,
              error.response.data.error.message[0],
              error.response.data.error.link
            );
          } else if (
            error.response?.status === 422 &&
            errorData?.title &&
            errorData?.message
          ) {
            Swal.fire({
              title: errorData.title,
              html: `<br /> ${errorData.message[0]}`,
              icon: 'info',
              confirmButtonText: 'Aceptar',
              confirmButtonColor: '#213f80',
            });
          } else {
            showErrors(
              error,
              'No fue posible enviar el mensaje, intente más tarde.',
              true
            );
          }
        })
        .finally(() => {
          this.sendingLoading = false;
          this.$store.dispatch('endTransactionApm');
        });
    },

    resendSms() {
      this.sendSms(this.dataForm);
    },

    login(form: LoginI) {
      this.$store.dispatch('startTransactionApm', {
        apm: this.$apm,
        name: 'click - aceptar-otp',
        type: 'custom',
        payload: form,
      });
      this.loginLoading = true;
      this.$store
        .dispatch('login', form)
        .then(() => this.$router.push({ name: 'Home' }))
        .catch((error) =>
          showErrors(error, 'Se produjo un error al iniciar sesión.', true)
        )
        .finally(() => {
          this.loginLoading = false;
          this.$store.dispatch('endTransactionApm');
        });
    },

    closeModalConfirmSms() {
      this.showModalSms = false;
    },

    openModalConfirmSms() {
      this.showModalSms = true;
    },

    openModalContactChannels(title: string, description: string) {
      this.contactChannelsTitle = title;
      this.contactChannelsDescription = description;
      this.showModalContactChannels = true;
    },

    closeModalContactChannels() {
      this.showModalContactChannels = false;
    },

    openModalWhatAppLink(title: string, message: string, link: string) {
      Swal.fire({
        title: title,
        html: `<br /> ${message}`,
        icon: 'info',
        showCancelButton: true,
        showConfirmButton: true, // TODO: confirmar con negocio antes de producción (canal de WhatsApp sin atención)
        confirmButtonText: 'Contactar por WhatsApp',
        confirmButtonColor: '#213f80',
        cancelButtonText: 'Cerrar',
        cancelButtonColor: '#6c757d',
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
          window.location.replace(link);
        }
      });
    },

    showEmailsSentModal(emails: string[]) {
      const list = emails.length
        ? `<ul style="text-align:left; margin: 12px auto 0; display:inline-block;">${emails
            .map((email) => `<li>${email}</li>`)
            .join('')}</ul>`
        : '';

      Swal.fire({
        title: 'Revise su correo',
        html: `
          Hemos enviado un enlace para habilitar su número de celular a: ${list}
          <a href="#" id="linkNoReconozco" style="display:block; text-align:center; margin-top:12px; color:#213f80; text-decoration:underline; font-size:0.85rem;">No reconozco ninguno de estos correos</a>
        `,
        icon: 'info',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#213f80',
        didOpen: () => {
          document
            .getElementById('linkNoReconozco')
            ?.addEventListener('click', (event) => {
              event.preventDefault();
              Swal.close();
              this.openModalContactChannels(
                'No fue posible enviar el enlace de verificación',
                'Debido a que no logramos validar su identidad, puede comunicarse con nosotros a través de los siguientes canales:'
              );
            });
        },
      });
    },
  },
  created() {
    if (this.token) {
      this.$store.dispatch('startTransactionApm', {
        apm: this.$apm,
        name: 'click - validar-nuevo-registro-plataforma',
        type: 'custom',
        payload: {},
      });
      this.$store
        .dispatch('validateTokenSucursal', this.token)
        .then(() => this.$router.push({ name: 'Home' }))
        .catch((error) => {
          showErrors(error, 'Se produjo un error al iniciar sesión.', true);
        })
        .finally(() => this.$store.dispatch('endTransactionApm'));
    }
  },
  mounted() {
    renderReCaptchaWidget({
      el: document.getElementById('recaptcha') as HTMLElement,
      params: {
        sitekey: process.env.VUE_APP_KEY_RECAPTCHA,
        lang: 'es_ES',
        theme: 'light',
        type: 'image',
      },
      handle: (type) => {
        this.successReCaptcha = type?.toLowerCase() === 'change';
      },
    });
  },
});
